<template>
  <div>
    <!-- Filtros -->
    <b-card class="mb-2">
      <b-card-header><h5 class="mb-0">Razão Analítico</h5></b-card-header>
      <b-card-body>
        <b-row>
          <b-col cols="12" md="3">
            <b-form-group label="Data Inicial">
              <b-form-input v-model="filtro.dataInicio" type="date" />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="3">
            <b-form-group label="Data Final">
              <b-form-input v-model="filtro.dataFim" type="date" />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="4">
            <b-form-group label="Plano de Conta">
              <v-select
                v-model="filtro.planoContaId"
                :options="comboPlanoConta"
                :reduce="o => o.id"
                :get-option-label="o => `${o.codNatureza || ''} - ${o.classe || ''}`"
                placeholder="Todos"
                clearable
              />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="2" class="d-flex align-items-end">
            <b-button variant="primary" class="w-100" :disabled="loading" @click="buscar">
              <b-spinner v-if="loading" small class="mr-1" />
              <feather-icon v-else icon="SearchIcon" class="mr-1" />
              Filtrar
            </b-button>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <!-- Tabela -->
    <b-card v-if="dados">
      <b-card-header class="d-flex justify-content-between align-items-center">
        <div>
          <h5 class="mb-0">Razão Analítico</h5>
          <small class="text-muted">{{ formatDate(dados.dataInicio) }} a {{ formatDate(dados.dataFim) }}</small>
        </div>
        <b-button variant="success" size="sm" @click="exportarCsv">
          <feather-icon icon="DownloadIcon" class="mr-1" />CSV
        </b-button>
      </b-card-header>
      <b-card-body>
        <b-table :items="dados.dados" :fields="campos" striped responsive hover small>
          <template #cell(totalReceitas)="{ item }">
            <span class="text-success">{{ formatMoeda(item.totalReceitas) }}</span>
          </template>
          <template #cell(totalDespesas)="{ item }">
            <span class="text-danger">{{ formatMoeda(item.totalDespesas) }}</span>
          </template>
          <template #cell(saldo)="{ item }">
            <span :class="item.saldo >= 0 ? 'text-success' : 'text-danger'">{{ formatMoeda(item.saldo) }}</span>
          </template>
          <template #custom-foot>
            <b-tr class="font-weight-bold table-active">
              <b-td colspan="2">TOTAL</b-td>
              <b-td class="text-success text-right">{{ formatMoeda(dados.totalReceitas) }}</b-td>
              <b-td class="text-danger text-right">{{ formatMoeda(dados.totalDespesas) }}</b-td>
              <b-td :class="dados.saldoTotal >= 0 ? 'text-success text-right' : 'text-danger text-right'">{{ formatMoeda(dados.saldoTotal) }}</b-td>
            </b-tr>
          </template>
        </b-table>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BCardHeader, BCardBody, BRow, BCol, BFormGroup, BFormInput,
  BButton, BSpinner, BTable, BTr, BTd
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import contabilServices from '@/api/financialModule/contabilServices'
import mixinsGlobal from '@/mixins'

export default {
  name: 'RazaoAnaliticoReport',
  components: { BCard, BCardHeader, BCardBody, BRow, BCol, BFormGroup, BFormInput, BButton, BSpinner, BTable, BTr, BTd, vSelect },
  mixins: [mixinsGlobal],
  data() {
    const hoje = new Date()
    return {
      filtro: {
        dataInicio: `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}-01`,
        dataFim: hoje.toISOString().substring(0, 10),
        planoContaId: null
      },
      dados: null,
      loading: false,
      comboPlanoConta: [],
      campos: [
        { key: 'codigo', label: 'Código' },
        { key: 'nome', label: 'Plano de Conta' },
        { key: 'totalReceitas', label: 'Total Receitas', tdClass: 'text-right' },
        { key: 'totalDespesas', label: 'Total Despesas', tdClass: 'text-right' },
        { key: 'saldo', label: 'Saldo', tdClass: 'text-right' }
      ]
    }
  },
  mounted() {
    this.carregarPlanoConta()
  },
  methods: {
    async carregarPlanoConta() {
      try {
        const res = await this.$store.dispatch('combosModule/loadComboPlanoContas')
        this.comboPlanoConta = res?.data || []
      } catch {}
    },
    async buscar() {
      this.loading = true
      try {
        const res = await contabilServices.getRazaoAnalitico({
          dataInicio: this.filtro.dataInicio,
          dataFim: this.filtro.dataFim,
          planoContaId: this.filtro.planoContaId || undefined
        })
        this.dados = res.data
      } catch {
        this.MensagemError('Erro', 'Falha ao gerar Razão Analítico.')
      } finally { this.loading = false }
    },
    formatDate(d) {
      if (!d) return ''
      const parts = Array.isArray(d) ? d : String(d).split('-')
      return `${String(parts[2]).padStart(2,'0')}/${String(parts[1]).padStart(2,'0')}/${parts[0]}`
    },
    formatMoeda(v) {
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0)
    },
    exportarCsv() {
      if (!this.dados) return
      const h = ['Código', 'Plano de Conta', 'Total Receitas', 'Total Despesas', 'Saldo']
      const rows = this.dados.dados.map(d => [d.codigo, d.nome, d.totalReceitas, d.totalDespesas, d.saldo])
      rows.push(['TOTAL', '', this.dados.totalReceitas, this.dados.totalDespesas, this.dados.saldoTotal])
      const csv = [h, ...rows].map(r => r.join(';')).join('\n')
      const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a'); a.href = url; a.download = 'razao_analitico.csv'; a.click()
    }
  }
}
</script>
